import { DEFAULT_LOCALE, Locale } from '@/constants/route-config';
import i18n from '@/assets/i18n';

/**
 * Convert language code to its base form (en-GB -> en)
 */
export const simplifyLanguageCode = (code: string): string => {
  if (!code) return '';
  return code.split('-')[0];
};

/**
 * Get the currently active locale to use in paths (always simplified to base form)
 */
export const getActiveLocale = (): string => {
  return simplifyLanguageCode(i18n.language || DEFAULT_LOCALE);
};

/**
 * Generate a localized path by adding the locale prefix
 * Uses the current i18n language if no locale is provided
 */
export const getLocalizedPath = (path: string, locale?: Locale): string => {
  // Always use simplified locale codes in paths
  const activeLocale = locale
    ? simplifyLanguageCode(locale)
    : getActiveLocale();

  // Check if the path already contains a locale prefix
  if (hasLocalePrefix(path)) {
    const { path: pathWithoutLocale } = extractLocaleFromPath(path);
    return `/${activeLocale}${pathWithoutLocale}`;
  }

  if (path === '/') return `/${activeLocale}`;
  return `/${activeLocale}${path}`;
};

/**
 * Extract locale from path
 * @returns Object with locale and path without locale
 */
export const extractLocaleFromPath = (
  path: string,
): { locale: Locale | null; path: string } => {
  const defaultReturn = { locale: null, path };

  // Check if path starts with a slash followed by a 2-letter locale code
  // We only support simple locale codes in URLs (en,zh, etc.)
  const match = path.match(/^\/([a-z]{2})(?:\/|$)/);
  if (!match) return defaultReturn;

  const localeCode = match[1];

  // Only validate simple 2-letter codes as we want to standardize URL structure
  const supportedLocales = i18n.languages
    .map(simplifyLanguageCode)
    .filter(Boolean);
  if (!supportedLocales.includes(localeCode)) {
    return defaultReturn;
  }

  // Remove locale from path
  const pathWithoutLocale = path.replace(/^\/[a-z]{2}/, '') || '/';
  return { locale: localeCode as Locale, path: pathWithoutLocale };
};

/**
 * Check if a path already has a locale prefix
 * @param path Path to check
 * @returns true if the path already has a valid locale prefix
 */
export const hasLocalePrefix = (path: string): boolean => {
  // Check if path starts with a slash followed by a 2-letter locale code
  // and that code is a supported locale
  const match = path.match(/^\/([a-z]{2})(?:\/|$)/);
  if (!match) return false;

  const localeCode = match[1];
  const supportedLocales = i18n.languages
    .map(simplifyLanguageCode)
    .filter(Boolean);
  return supportedLocales.includes(localeCode);
};

/**
 * Clean up a path that might have multiple locale prefixes
 * @param path Path to clean
 * @returns Clean path with at most one locale prefix
 */
export const cleanupLocalePath = (path: string): string => {
  const segments = path.split('/').filter(Boolean);
  const supportedLocales = i18n.languages
    .map(simplifyLanguageCode)
    .filter(Boolean);

  const localeIndex = segments.findIndex(
    (seg) => supportedLocales.includes(seg) && seg.length === 2,
  );

  if (localeIndex === -1) return path;

  const cleanSegments = segments.filter(
    (seg, index) =>
      index === localeIndex ||
      !supportedLocales.includes(seg) ||
      seg.length !== 2,
  );

  return '/' + cleanSegments.join('/');
};
